import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Button, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, Skeleton,
  DialogTitle, TextField, Tooltip, Typography,
} from "@mui/material";
import { AddOutlined, DeleteOutlined, DragIndicatorOutlined, EditOutlined, HelpOutlineOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import { api } from "../../services/api";
import EditorTexto from "../EditorTexto";
import BotonCircular from "../ui/BotonCircular";
import { sanitizarHtml } from "../../utils/sanitizarHtml";

const PRIMARIO = "#5656FF";
const TITULO = "#0b0060";
const FUENTE = "Bricolage Grotesque";
const NUEVA = "nueva";

const vacia = { pregunta: "", respuesta: "" };

// mismos límites que controllers/faqs
const MAX_PREGUNTA = 200;
const MAX_POR_EVENTO = 30;
const MAX_RESPUESTA = 2000;
const LARGO_EXTRACTO = 90;

// "<p><br></p>" queda vacío
const textoDe = (html) => (html || "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();

const etiquetaSx = { fontSize: 12, fontWeight: 600, display: "block", mb: 0.75, fontFamily: FUENTE };

const extracto = (html) => {
  const texto = textoDe(html);
  return texto.length > LARGO_EXTRACTO ? `${texto.slice(0, LARGO_EXTRACTO).trimEnd()}…` : texto;
};

const Contador = ({ actual, maximo }) => (
  <Typography sx={{ fontSize: 11, textAlign: "right", mt: 0.5, color: actual > maximo ? "#ef4444" : "text.secondary" }}>
    {actual}/{maximo}
  </Typography>
);

const FormularioFaq = ({ form, setForm, guardando, onGuardar, onCancelar }) => (
  <Box sx={{ px: 2, pb: 2, pt: 0.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
    <div>
      <Typography component="label" sx={etiquetaSx}>Pregunta</Typography>
      <TextField
        fullWidth
        size="small"
        autoFocus
        placeholder="¿A qué hora abren las puertas?"
        value={form.pregunta}
        onChange={(e) => setForm((f) => ({ ...f, pregunta: e.target.value }))}
        inputProps={{ maxLength: MAX_PREGUNTA }}
        sx={{ "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: PRIMARIO } }}
      />
      <Contador actual={form.pregunta.length} maximo={MAX_PREGUNTA} />
    </div>
    <div>
      <Typography component="label" sx={etiquetaSx}>Respuesta</Typography>
      <EditorTexto
        conTitulos={false}
        placeholder="Escribí la respuesta..."
        value={form.respuesta}
        onChange={(valor) => setForm((f) => ({ ...f, respuesta: valor }))}
      />
      <Contador actual={textoDe(form.respuesta).length} maximo={MAX_RESPUESTA} />
    </div>
    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
      <Button onClick={onCancelar} disabled={guardando} sx={{ textTransform: "none", color: "#374151" }}>
        Cancelar
      </Button>
      <Button
        variant="contained"
        onClick={onGuardar}
        disabled={guardando}
        sx={{ bgcolor: PRIMARIO, textTransform: "none", "&:hover": { bgcolor: "#4545ee" } }}
      >
        {guardando && <CircularProgress size={16} sx={{ mr: 1, color: "white" }} />}
        Guardar
      </Button>
    </Box>
  </Box>
);

const GestionFaqs = ({ eventoId, soloLectura = false, compacto = false, className = "mt-5" }) => {
  const [faqs, setFaqs] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [abierta, setAbierta] = useState(null); // id de la fila desplegada, NUEVA o null
  const [form, setForm] = useState(vacia);
  const [guardando, setGuardando] = useState(false);
  const [aEliminar, setAEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);

  const arrastrada = useRef(null);
  const [origen, setOrigen] = useState(null);
  const [destino, setDestino] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const response = await api.faqs.getAll(eventoId);
      if (response.status === "success") setFaqs(response.data?.FAQS ?? []);
    } catch (err) {
      console.error("Error al cargar las preguntas frecuentes:", err);
    } finally {
      setCargando(false);
    }
  }, [eventoId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const cerrar = () => {
    setAbierta(null);
    setForm(vacia);
  };

  const abrirNueva = () => {
    setForm(vacia);
    setAbierta(NUEVA);
  };

  const alternar = (faq) => {
    if (abierta === faq.id) {
      cerrar();
      return;
    }
    setForm({ pregunta: faq.pregunta, respuesta: faq.respuesta });
    setAbierta(faq.id);
  };

  const guardar = async () => {
    if (!form.pregunta.trim() || !textoDe(form.respuesta)) {
      toast.error("Completá la pregunta y la respuesta.");
      return;
    }
    if (textoDe(form.respuesta).length > MAX_RESPUESTA) {
      toast.error(`La respuesta no puede superar ${MAX_RESPUESTA} caracteres.`);
      return;
    }
    const esNueva = abierta === NUEVA;
    setGuardando(true);
    try {
      const response = esNueva
        ? await api.faqs.createFaqs(eventoId, form)
        : await api.faqs.editFaqs(eventoId, abierta, form);
      if (response.status !== "success") {
        toast.error(response.message || "No se pudo guardar la pregunta.");
        return;
      }
      toast.success(esNueva ? "Pregunta creada" : "Pregunta actualizada");
      cerrar();
      cargar();
    } catch (err) {
      toast.error("No se pudo conectar con el servidor.");
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async () => {
    setEliminando(true);
    try {
      const response = await api.faqs.deleteFaqs(eventoId, aEliminar.id);
      if (response.status !== "success") {
        toast.error(response.message || "No se pudo eliminar la pregunta.");
        return;
      }
      toast.success("Pregunta eliminada");
      if (abierta === aEliminar.id) cerrar();
      setAEliminar(null);
      cargar();
    } catch (err) {
      toast.error("No se pudo conectar con el servidor.");
    } finally {
      setEliminando(false);
    }
  };

  // optimista: si falla el guardado se recarga el orden real
  const soltar = async (e, objetivo) => {
    e.preventDefault();
    const idOrigen = arrastrada.current;
    arrastrada.current = null;
    setOrigen(null);
    setDestino(null);
    if (!idOrigen || idOrigen === objetivo.id) return;

    const desde = faqs.findIndex((f) => f.id === idOrigen);
    const hasta = faqs.findIndex((f) => f.id === objetivo.id);
    if (desde === -1 || hasta === -1) return;

    const reordenadas = [...faqs];
    const [movida] = reordenadas.splice(desde, 1);
    reordenadas.splice(hasta, 0, movida);
    setFaqs(reordenadas);

    try {
      const response = await api.faqs.reordenarFaqs(eventoId, reordenadas.map((f) => f.id));
      if (response.status !== "success") throw new Error(response.message);
    } catch (err) {
      toast.error("No se pudo guardar el orden.");
      cargar();
    }
  };

  const terminarArrastre = () => {
    arrastrada.current = null;
    setOrigen(null);
    setDestino(null);
  };

  const sePuedeArrastrar = !soloLectura && abierta === null && faqs.length > 1;

  const propsFormulario = { form, setForm, guardando, onGuardar: guardar, onCancelar: cerrar };
  const vacio = !cargando && faqs.length === 0 && abierta !== NUEVA;

  return (
    <div className={className}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: compacto ? "space-between" : "flex-end", flexWrap: "wrap", gap: 1.5, mb: compacto ? 1 : 2 }}>
        {compacto && (
          <Typography sx={{ fontFamily: FUENTE, fontWeight: 700, color: TITULO, fontSize: 20, lineHeight: 1.2 }}>
            Preguntas frecuentes
          </Typography>
        )}
        {!soloLectura && (
          <Tooltip title={faqs.length >= MAX_POR_EVENTO ? `Llegaste al máximo de ${MAX_POR_EVENTO} preguntas por evento` : ""}>
            {/* el span deja mostrar el tooltip con el botón deshabilitado */}
            <span>
              <Button
                variant="contained"
                startIcon={<AddOutlined />}
                onClick={abrirNueva}
                disabled={abierta === NUEVA || faqs.length >= MAX_POR_EVENTO}
                sx={{ bgcolor: PRIMARIO, textTransform: "none", fontFamily: FUENTE, "&:hover": { bgcolor: "#4545ee" } }}
              >
                Nueva pregunta
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      {compacto && (
        <Typography sx={{ color: "text.secondary", fontSize: 13, mb: 2.5 }}>
          Se muestran en la página pública del evento. Respondé lo que más te consultan: horarios, accesos, edad mínima...
          {!soloLectura && " Arrastrá para reordenarlas."}
        </Typography>
      )}

      <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #f0f0f0", overflow: "hidden" }}>
        {cargando && (
          <Box sx={{ p: 2 }}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} variant="rounded" height={44} sx={{ mb: i < 2 ? 1 : 0 }} />
            ))}
          </Box>
        )}

        <Collapse in={abierta === NUEVA} unmountOnExit>
          <Box sx={{ borderBottom: faqs.length ? "1px solid #f3f4f6" : "none", bgcolor: "#fafaff" }}>
            <Typography sx={{ px: 2, pt: 1.5, pb: 0.5, fontWeight: 600, fontSize: 14, color: PRIMARIO, fontFamily: FUENTE }}>
              Nueva pregunta
            </Typography>
            <FormularioFaq {...propsFormulario} />
          </Box>
        </Collapse>

        {vacio && (
          <Box sx={{ py: 5, textAlign: "center" }}>
            <HelpOutlineOutlined sx={{ fontSize: 40, color: "#d1d5db", mb: 1.5 }} />
            <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
              {soloLectura
                ? "Este evento todavía no tiene preguntas frecuentes."
                : "No hay preguntas todavía. Creá la primera para la página del evento."}
            </Typography>
          </Box>
        )}

        {!cargando && faqs.map((faq, i) => {
          const desplegada = abierta === faq.id;
          return (
            <Box
              key={faq.id}
              draggable={sePuedeArrastrar}
              onDragStart={(e) => {
                arrastrada.current = faq.id;
                setOrigen(faq.id);
                e.dataTransfer.effectAllowed = "move";
              }}
              onDragOver={(e) => {
                if (!arrastrada.current) return;
                e.preventDefault();
                if (faq.id !== arrastrada.current) setDestino(faq.id);
              }}
              onDragLeave={() => setDestino(null)}
              onDrop={(e) => soltar(e, faq)}
              onDragEnd={terminarArrastre}
              sx={{
                borderBottom: i === faqs.length - 1 ? "none" : "1px solid #f3f4f6",
                borderTop: destino === faq.id ? `2px solid ${PRIMARIO}` : undefined,
                bgcolor: destino === faq.id ? "#f5f5ff" : desplegada && !soloLectura ? "#fafaff" : "#fff",
                opacity: origen === faq.id ? 0.4 : 1,
                transition: "background 0.1s, opacity 0.1s",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2, py: 1.4 }}>
                {!soloLectura && (
                  <DragIndicatorOutlined
                    sx={{
                      color: "#d1d5db",
                      fontSize: 18,
                      flexShrink: 0,
                      cursor: sePuedeArrastrar ? "grab" : "default",
                      opacity: sePuedeArrastrar ? 1 : 0.4,
                    }}
                  />
                )}
                <Box sx={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => alternar(faq)}>
                  <Typography noWrap sx={{ fontWeight: 600, fontSize: 14, color: PRIMARIO, fontFamily: FUENTE }}>
                    {faq.pregunta}
                  </Typography>
                  {!desplegada && (
                    <Typography noWrap sx={{ fontSize: 12, color: "text.secondary", maxWidth: 560 }}>
                      {extracto(faq.respuesta)}
                    </Typography>
                  )}
                </Box>

                {!soloLectura && (
                  <Box sx={{ display: "flex", gap: 0.75 }}>
                    <BotonCircular icono={<EditOutlined fontSize="small" />} titulo="Editar" onClick={() => alternar(faq)} />
                    <BotonCircular icono={<DeleteOutlined fontSize="small" />} titulo="Eliminar" peligro onClick={() => setAEliminar(faq)} />
                  </Box>
                )}
              </Box>

              <Collapse in={desplegada} unmountOnExit>
                {soloLectura ? (
                  /* pre-line respeta los saltos de las respuestas viejas, de texto plano */
                  <Box
                    className="descripcion-unstyled"
                    sx={{ px: 2, pb: 1.5, color: "#4b4d63", fontSize: 14, whiteSpace: "pre-line" }}
                    dangerouslySetInnerHTML={{ __html: sanitizarHtml(faq.respuesta) }}
                  />
                ) : (
                  <FormularioFaq {...propsFormulario} />
                )}
              </Collapse>
            </Box>
          );
        })}
      </Box>

      <Dialog open={Boolean(aEliminar)} onClose={() => !eliminando && setAEliminar(null)}>
        <DialogTitle sx={{ fontFamily: FUENTE, fontSize: 18 }}>¿Eliminar pregunta?</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{aEliminar?.pregunta}</strong> se va a eliminar de la página del evento.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAEliminar(null)} disabled={eliminando} sx={{ textTransform: "none", color: "#374151" }}>
            Cancelar
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={eliminar}
            disabled={eliminando}
            sx={{ textTransform: "none" }}
          >
            {eliminando ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default GestionFaqs;
